import { useMutation, useQuery } from "@tanstack/react-query";
import EmployerRegistrationService from "@/services/employer-registration/employer-registration.service";
import type { LookupRCDto } from "@/models/dto/lookup-rc.dto";
import type { RegisterEmployerFormDto } from "@/models/dto/register-employer.dto";
import type { ResendEmployerOTPDto } from "@/models/dto/resend-employer-otp.dto";
import type { ValidateTinDto } from "@/models/dto/validate-tin.dto";
import type { VerifyEmployerOTPDto } from "@/models/dto/verify-employer-otp.dto";
import type { BaseResponse } from "@/models/responses/base/baseResponse.response";
import type {
  RCLookupResponse,
  SectorCode,
  OrganizationType,
  SectorsByOrgType,
  ValidateTinResult
} from "@/models/responses/employer-registration.response";

export const useLookupRC = () => {
  return useMutation<RCLookupResponse, Error, LookupRCDto>({
    mutationFn: (dto) => EmployerRegistrationService.lookupRC(dto),
  });
};

export const useValidateTin = () => {
  return useMutation<ValidateTinResult, Error, ValidateTinDto>({
    mutationFn: (dto) => EmployerRegistrationService.validateTin(dto),
  });
};

export const useRegisterEmployer = () => {
  return useMutation<BaseResponse<unknown>, Error, RegisterEmployerFormDto>({
    mutationFn: (dto) => EmployerRegistrationService.registerEmployer(dto),
  });
};

export const useResendEmployerOTP = () => {
  return useMutation<BaseResponse<unknown>, Error, ResendEmployerOTPDto>({
    mutationFn: (dto) => EmployerRegistrationService.resendOTP(dto),
  });
};

export const useVerifyEmployerOTP = () => {
  return useMutation<BaseResponse<unknown>, Error, VerifyEmployerOTPDto>({
    mutationFn: (dto) => EmployerRegistrationService.verifyOTP(dto),
  });
};

export const useGetSectorCodes = () => {
  return useMutation<SectorCode[], Error, void>({
    mutationFn: () => EmployerRegistrationService.getSectorCodes(),
  });
};

export const useGetOrganizationTypes = () => {
  return useQuery<OrganizationType[], Error>({
    queryKey: ['organization-types'],
    queryFn: () => EmployerRegistrationService.getOrganizationTypes(),
  });
};

export const useGetSectorCodesByOrgType = (typeCode: string) => {
  return useQuery<SectorsByOrgType, Error>({
    queryKey: ['sector-codes', typeCode],
    queryFn: () => EmployerRegistrationService.getSectorCodesByOrgType(typeCode),
    enabled: !!typeCode,
  });
};
